import { Component, OnInit, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ExportModalComponent, ExportOptions, ExportColumn } from '../../../shared/components/export-modal/export-modal.component';
import { DateFormatPipe, StatusBadgePipe } from '../../../shared/pipes';
import { SprayRecordService, SprayRecordListItem } from '../../../core/services/spray-record.service';

interface FilterState {
  status: string;
  field: string;
  pilot: string;
  dateFrom: string;
  dateTo: string;
}

@Component({
  selector: 'app-spray-records',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ConfirmModalComponent, ExportModalComponent, DateFormatPipe, StatusBadgePipe],
  templateUrl: './spray-records.component.html',
  styleUrls: ['./spray-records.component.scss']
})
export class SprayRecordsComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;
  @ViewChild('exportModal') exportModal!: ExportModalComponent;

  // Inject SprayRecordService
  private sprayRecordService = inject(SprayRecordService);

  // Signal-based state
  private filtersSignal = signal<FilterState>({
    status: '',
    field: '',
    pilot: '',
    dateFrom: '',
    dateTo: ''
  });

  // Computed signals from service
  readonly isLoading = this.sprayRecordService.isLoading;

  // Get records as list items and apply local filtering
  readonly sprayRecords = computed(() => this.sprayRecordService.getRecordsAsListItems());

  readonly filteredRecords = computed(() => {
    const filters = this.filtersSignal();
    let results = this.sprayRecords();

    if (filters.status) {
      results = results.filter(r => r.status === filters.status);
    }
    if (filters.field) {
      results = results.filter(r =>
        r.fieldName.toLowerCase().includes(filters.field.toLowerCase())
      );
    }
    if (filters.pilot) {
      results = results.filter(r =>
        r.pilotName.toLowerCase().includes(filters.pilot.toLowerCase())
      );
    }
    if (filters.dateFrom) {
      results = results.filter(r => r.date >= filters.dateFrom);
    }
    if (filters.dateTo) {
      results = results.filter(r => r.date <= filters.dateTo);
    }

    return results;
  });

  // Computed stats from service
  readonly completedRecords = computed(() =>
    this.sprayRecords().filter(r => r.status === 'completed')
  );

  readonly pendingRecords = computed(() =>
    this.sprayRecords().filter(r => r.status === 'pending')
  );

  readonly totalAcreage = computed(() =>
    this.completedRecords().reduce((sum, r) => sum + r.acreage, 0)
  );

  // Unique values for filter dropdowns (computed)
  readonly uniqueFields = computed(() =>
    [...new Set(this.sprayRecords().map(r => r.fieldName))]
  );

  readonly uniquePilots = computed(() =>
    [...new Set(this.sprayRecords().map(r => r.pilotName))]
  );

  // UI state (local signals)
  isFilterOpen = signal(false);
  activeFilterCount = signal(0);

  // For delete confirmation
  recordToDelete: SprayRecordListItem | null = null;

  // Export columns configuration
  exportColumns: ExportColumn[] = [
    { key: 'date', label: 'Date', selected: true },
    { key: 'fieldName', label: 'Field Name', selected: true },
    { key: 'cropType', label: 'Crop Type', selected: true },
    { key: 'acreage', label: 'Acreage', selected: true },
    { key: 'chemicalName', label: 'Chemical', selected: true },
    { key: 'applicationRate', label: 'Application Rate', selected: true },
    { key: 'pilotName', label: 'Pilot', selected: true },
    { key: 'droneId', label: 'Drone ID', selected: true },
    { key: 'status', label: 'Status', selected: true },
    { key: 'weatherConditions', label: 'Weather', selected: false },
    { key: 'notes', label: 'Notes', selected: false }
  ];

  private dateFormatPipe = new DateFormatPipe();

  // Expose filters for template reading
  get filters(): FilterState {
    return this.filtersSignal();
  }

  constructor(private router: Router) {}

  // Update a single filter field and re-trigger computed signals
  updateFilter(key: keyof FilterState, value: string): void {
    this.filtersSignal.update(current => ({ ...current, [key]: value }));
    this.updateActiveFilterCount();
  }

  ngOnInit(): void {
    // Data is already loaded via service constructor
    // No need for loadSprayRecords() - service handles it
  }

  // Filter methods
  toggleFilter(): void {
    this.isFilterOpen.update(open => !open);
  }

  updateActiveFilterCount(): void {
    const filters = this.filtersSignal();
    let count = 0;
    if (filters.status) count++;
    if (filters.field) count++;
    if (filters.pilot) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    this.activeFilterCount.set(count);
  }

  clearFilters(): void {
    this.filtersSignal.set({
      status: '',
      field: '',
      pilot: '',
      dateFrom: '',
      dateTo: ''
    });
    this.activeFilterCount.set(0);
  }

  // Export methods
  openExportModal(): void {
    const count = this.filteredRecords().length;
    this.exportModal.open({
      title: 'Export Spray Records',
      description: `Export ${count} spray record${count !== 1 ? 's' : ''} based on current filters.`,
      formats: ['csv', 'pdf', 'excel'],
      columns: this.exportColumns,
      showDateRange: true,
      showColumnSelection: true
    });
  }

  onExport(options: ExportOptions): void {
    console.log('Export requested with options:', options);
    // TODO: Implement actual export logic
    const count = this.filteredRecords().length;
    alert(`Exporting ${count} records as ${options.format.toUpperCase()}`);
  }

  addSprayRecord(): void {
    this.router.navigate(['/logbook/spray-records/add']);
  }

  viewRecord(record: SprayRecordListItem): void {
    this.router.navigate(['/logbook/spray-records', record.id, 'view']);
  }

  editRecord(record: SprayRecordListItem): void {
    this.router.navigate(['/logbook/spray-records', record.id, 'edit']);
  }

  deleteRecord(record: SprayRecordListItem): void {
    this.recordToDelete = record;
    this.confirmModal.open({
      title: 'Delete Spray Record',
      message: `Are you sure you want to delete the spray record for "${record.fieldName}" on ${this.dateFormatPipe.transform(record.date)}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      icon: 'bi-trash'
    });
  }

  onDeleteConfirmed(): void {
    if (this.recordToDelete) {
      // Delete via service
      this.sprayRecordService.deleteRecord(this.recordToDelete.id);
      this.recordToDelete = null;
    }
  }

  onDeleteCancelled(): void {
    this.recordToDelete = null;
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'completed':
        return 'Completed';
      case 'pending':
        return 'Pending';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  }
}
